import { useEffect, useState } from "react";
import { MousePointerClick, Users, Clock, CheckCircle2, XCircle, IndianRupee, Wallet, Settings, CreditCard, Landmark } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fmtDateTime } from "../../lib/api";
import { StatCard, PageHeader, StatusBadge, TableShell, TR, TD, LoadingRows, EmptyState } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const TABS = [
  { k: "payments", l: "Payment Verification", icon: CreditCard },
  { k: "settings", l: "Self-Service Settings", icon: Settings },
  { k: "payouts", l: "Partner Payouts", icon: Landmark },
];

const FILTERS = ["", "pending", "needs_info", "approved", "rejected"];

function Modal({ title, children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={onClose}>
      <div data-testid={testid} className="bg-card border border-border rounded-2xl p-6 w-full max-w-md space-y-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display font-bold text-lg">{title}</h3>
        {children}
      </div>
    </div>
  );
}

export default function RootSelfService() {
  const [tab, setTab] = useState("payments");
  const [overview, setOverview] = useState(null);
  const [payments, setPayments] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [settings, setSettings] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [modal, setModal] = useState(null); // {mode: 'reject'|'needs_info'|'payout', ...}
  const [note, setNote] = useState("");
  const [payForm, setPayForm] = useState({ amount: "", method: "", reference: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const loadOverview = () => api.get("/root/self-service/overview").then((r) => setOverview(r.data)).catch((e) => toast.error(errMsg(e)));
  const loadPayments = (f = filter) => api.get(`/root/site-payments${f ? `?status=${f}` : ""}`).then((r) => setPayments(r.data.payments)).catch((e) => toast.error(errMsg(e)));
  const loadSettings = () => api.get("/root/self-service/settings").then((r) => setSettings(r.data)).catch((e) => toast.error(errMsg(e)));
  const loadPayouts = () => api.get("/root/partner-payouts").then((r) => setPayouts(r.data)).catch((e) => toast.error(errMsg(e)));

  useEffect(() => { loadOverview(); loadPayments("pending"); }, []);
  useEffect(() => {
    if (tab === "settings" && !settings) loadSettings();
    if (tab === "payouts" && !payouts) loadPayouts();
  }, [tab]);

  const changeFilter = (f) => { setFilter(f); setPayments(null); loadPayments(f); };

  const approve = async (p) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/root/site-payments/${p.id}/approve`);
      toast.success(`Approved — commission ${fmtINR(data.commission)} recorded`);
      loadPayments(); loadOverview();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const submitNote = async () => {
    setBusy(true);
    try {
      await api.post(`/root/site-payments/${modal.payment.id}/${modal.mode}`, { note });
      toast.success(modal.mode === "reject" ? "Payment rejected" : "Customer asked for more info");
      setModal(null); setNote("");
      loadPayments(); loadOverview();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/root/self-service/settings", settings);
      setSettings(data);
      toast.success("Settings saved");
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  const recordPayout = async () => {
    setBusy(true);
    try {
      await api.post("/root/partner-payouts", { partner_id: modal.partner.partner_id, ...payForm, amount: Number(payForm.amount) });
      toast.success("Payout recorded");
      setModal(null); setPayForm({ amount: "", method: "", reference: "", notes: "" });
      loadPayouts(); loadOverview();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="root-self-service-page">
      <PageHeader title="Self-Service Sales" subtitle="Referral-driven website creation — verify payments, configure pricing, manage partner commissions." testid="root-self-service-title" />

      {overview && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
          <StatCard icon={MousePointerClick} label="Referral Clicks" value={overview.clicks} testid="ss-stat-clicks" />
          <StatCard icon={Users} label="Signups" value={overview.signups} testid="ss-stat-signups" delay={0.05} />
          <StatCard icon={Clock} label="Pending Verification" value={overview.pending} testid="ss-stat-pending" delay={0.1} />
          <StatCard icon={CheckCircle2} label="Approved" value={overview.approved} testid="ss-stat-approved" delay={0.15} />
          <StatCard icon={IndianRupee} label="Gross Revenue" value={fmtINR(overview.gross)} testid="ss-stat-gross" delay={0.2} />
          <StatCard icon={Wallet} label="Company Revenue" value={fmtINR(overview.company_revenue)} testid="ss-stat-company" delay={0.25} />
          <StatCard icon={IndianRupee} label="Commission Owed" value={fmtINR(overview.payout_pending)} testid="ss-stat-commission" delay={0.3} />
          <StatCard icon={CheckCircle2} label="Payouts Completed" value={fmtINR(overview.payout_paid)} testid="ss-stat-paid" delay={0.35} />
        </div>
      )}

      <div className="flex gap-2 flex-wrap mb-6">
        {TABS.map((t) => (
          <button key={t.k} data-testid={`ss-tab-${t.k}`} onClick={() => setTab(t.k)}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold border transition-colors ${tab === t.k ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
            <t.icon className="h-4 w-4" /> {t.l}
          </button>
        ))}
      </div>

      {tab === "payments" && (
        <div>
          <div className="flex gap-2 flex-wrap mb-4">
            {FILTERS.map((f) => (
              <button key={f || "all"} data-testid={`pay-filter-${f || "all"}`} onClick={() => changeFilter(f)}
                className={`px-4 py-1.5 rounded-full text-xs font-bold border transition-colors ${filter === f ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}>
                {f ? f.replace(/_/g, " ") : "all"}
              </button>
            ))}
          </div>
          {payments === null ? (
            <div className="bg-card border border-border rounded-2xl p-8"><LoadingRows rows={3} /></div>
          ) : payments.length === 0 ? (
            <EmptyState icon={CreditCard} title="No payments here" body="Payment submissions matching this filter will appear here." />
          ) : (
            <TableShell head={["Customer", "Partner", "Website", "Amount", "Reference", "Status", "Submitted", "Actions"]} testid="site-payments-table">
              {payments.map((p) => (
                <TR key={p.id} testid={`site-payment-${p.id}`}>
                  <TD><p className="font-semibold">{p.customer_name}</p><p className="text-xs text-muted-foreground">{p.customer_email}</p>{p.customer_phone && <p className="text-xs text-muted-foreground">{p.customer_phone}</p>}</TD>
                  <TD>{p.partner_name || "—"}</TD>
                  <TD>{p.website_name || "—"}</TD>
                  <TD className="font-bold">{fmtINR(p.amount)}</TD>
                  <TD className="font-mono text-xs">{p.reference || "—"}{p.paid_at ? <p className="text-muted-foreground font-sans">{p.paid_at}</p> : null}</TD>
                  <TD><StatusBadge status={p.status} /></TD>
                  <TD className="text-sm text-muted-foreground">{fmtDateTime(p.created_at)}</TD>
                  <TD>
                    {["pending", "needs_info"].includes(p.status) ? (
                      <div className="flex gap-2">
                        <button data-testid={`approve-${p.id}`} disabled={busy} onClick={() => approve(p)}
                          className="h-8 px-3 rounded-lg bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 text-xs font-bold hover:bg-emerald-500/25 transition-colors">Approve</button>
                        <button data-testid={`needsinfo-${p.id}`} disabled={busy} onClick={() => { setModal({ mode: "needs-info", payment: p }); setNote(""); }}
                          className="h-8 px-3 rounded-lg bg-amber-500/15 text-amber-600 dark:text-amber-400 text-xs font-bold hover:bg-amber-500/25 transition-colors">Needs Info</button>
                        <button data-testid={`reject-${p.id}`} disabled={busy} onClick={() => { setModal({ mode: "reject", payment: p }); setNote(""); }}
                          className="h-8 px-3 rounded-lg bg-red-500/15 text-red-600 dark:text-red-400 text-xs font-bold hover:bg-red-500/25 transition-colors">Reject</button>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">{p.commission != null ? `Commission ${fmtINR(p.commission)}` : p.admin_note || "—"}</span>
                    )}
                  </TD>
                </TR>
              ))}
            </TableShell>
          )}
        </div>
      )}

      {tab === "settings" && (
        !settings ? <div className="bg-card border border-border rounded-2xl p-8"><LoadingRows rows={3} /></div> : (
          <form data-testid="ss-settings-form" onSubmit={saveSettings} className="bg-card border border-border rounded-2xl p-6 grid sm:grid-cols-2 gap-5 max-w-4xl">
            <label className="flex items-center gap-3 sm:col-span-2 cursor-pointer">
              <input data-testid="ss-enabled" type="checkbox" checked={Boolean(settings.enabled)} onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })} className="h-5 w-5 accent-primary" />
              <span className="text-sm font-bold">Self-service website creation enabled</span>
            </label>
            <div className="space-y-1.5">
              <Label className="overline-label">Website Price (₹)</Label>
              <Input data-testid="ss-price" type="number" min="0" value={settings.price} onChange={(e) => setSettings({ ...settings, price: e.target.value })} className="h-12 rounded-md bg-background" />
            </div>
            <div className="space-y-1.5">
              <Label className="overline-label">SuperProfile Payment Link</Label>
              <Input data-testid="ss-payment-link" value={settings.payment_link} onChange={(e) => setSettings({ ...settings, payment_link: e.target.value })} className="h-12 rounded-md bg-background" placeholder="https://superprofile.bio/..." />
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input data-testid="ss-offer-active" type="checkbox" checked={Boolean(settings.offer_active)} onChange={(e) => setSettings({ ...settings, offer_active: e.target.checked })} className="h-5 w-5 accent-primary" />
              <span className="text-sm font-bold">Offer price active</span>
            </label>
            <div className="space-y-1.5">
              <Label className="overline-label">Offer Price (₹)</Label>
              <Input data-testid="ss-offer-price" type="number" min="0" value={settings.offer_price} onChange={(e) => setSettings({ ...settings, offer_price: e.target.value })} className="h-12 rounded-md bg-background" />
            </div>
            <div className="space-y-1.5">
              <Label className="overline-label">Commission Type</Label>
              <select data-testid="ss-commission-type" value={settings.commission_type} onChange={(e) => setSettings({ ...settings, commission_type: e.target.value })} className="h-12 w-full rounded-md border border-input bg-background px-3 text-sm">
                <option value="fixed">Fixed amount (₹)</option>
                <option value="percent">Percentage (%)</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="overline-label">Commission Value {settings.commission_type === "percent" ? "(%)" : "(₹)"}</Label>
              <Input data-testid="ss-commission-value" type="number" min="0" value={settings.commission_value} onChange={(e) => setSettings({ ...settings, commission_value: e.target.value })} className="h-12 rounded-md bg-background" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="overline-label">Verification Message (shown to customer)</Label>
              <textarea data-testid="ss-verification-message" value={settings.verification_message} onChange={(e) => setSettings({ ...settings, verification_message: e.target.value })}
                className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm min-h-20" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="overline-label">Offer Description (optional)</Label>
              <textarea data-testid="ss-description" value={settings.description || ""} onChange={(e) => setSettings({ ...settings, description: e.target.value })}
                className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm min-h-20" />
            </div>
            <div className="sm:col-span-2">
              <Button data-testid="ss-settings-save" type="submit" disabled={busy} className="rounded-full font-bold">{busy ? "Saving..." : "Save Settings"}</Button>
            </div>
          </form>
        )
      )}

      {tab === "payouts" && (
        !payouts ? <div className="bg-card border border-border rounded-2xl p-8"><LoadingRows rows={3} /></div> : (
          <div className="space-y-10">
            <div>
              <h2 className="font-display font-bold text-2xl tracking-tight mb-4">Partner Balances</h2>
              {payouts.partners.length === 0 ? (
                <EmptyState icon={Landmark} title="No commissions yet" body="When referral payments get approved, partner balances appear here." />
              ) : (
                <TableShell head={["Partner", "Bank / UPI", "Earned", "Paid", "Pending", "Action"]} testid="payout-balances-table">
                  {payouts.partners.map((p) => (
                    <TR key={p.partner_id} testid={`payout-partner-${p.partner_id}`}>
                      <TD><p className="font-semibold">{p.partner_name}</p><p className="text-xs text-muted-foreground">{p.partner_email}</p></TD>
                      <TD className="text-xs text-muted-foreground">
                        {p.payout_details?.upi_id ? <p>UPI: {p.payout_details.upi_id}</p> : null}
                        {p.payout_details?.account_number ? <p>A/C {p.payout_details.account_number} · {p.payout_details.ifsc}</p> : null}
                        {!p.payout_details?.upi_id && !p.payout_details?.account_number && "—"}
                      </TD>
                      <TD className="font-semibold">{fmtINR(p.earned)}</TD>
                      <TD>{fmtINR(p.paid)}</TD>
                      <TD className="font-bold text-amber-600 dark:text-amber-400">{fmtINR(p.pending)}</TD>
                      <TD>
                        <button data-testid={`record-payout-${p.partner_id}`} disabled={p.pending <= 0}
                          onClick={() => { setModal({ mode: "payout", partner: p }); setPayForm({ amount: p.pending, method: "", reference: "", notes: "" }); }}
                          className="h-8 px-3 rounded-lg bg-primary text-primary-foreground text-xs font-bold hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed">
                          Record Payout
                        </button>
                      </TD>
                    </TR>
                  ))}
                </TableShell>
              )}
            </div>
            <div>
              <h2 className="font-display font-bold text-2xl tracking-tight mb-4">Payout History</h2>
              {payouts.payouts.length === 0 ? (
                <p className="text-sm text-muted-foreground border border-dashed border-border rounded-xl p-6">No payouts recorded yet.</p>
              ) : (
                <TableShell head={["Amount", "Method", "Reference", "Notes", "Date"]} testid="payout-history-table">
                  {payouts.payouts.map((p) => (
                    <TR key={p.id} testid={`payout-row-${p.id}`}>
                      <TD className="font-bold">{fmtINR(p.amount)}</TD>
                      <TD>{p.method || "—"}</TD>
                      <TD className="font-mono text-xs">{p.reference || "—"}</TD>
                      <TD className="text-sm text-muted-foreground">{p.notes || "—"}</TD>
                      <TD className="text-sm text-muted-foreground">{fmtDateTime(p.created_at)}</TD>
                    </TR>
                  ))}
                </TableShell>
              )}
            </div>
          </div>
        )
      )}

      {modal && ["reject", "needs-info"].includes(modal.mode) && (
        <Modal testid="payment-note-modal" title={modal.mode === "reject" ? `Reject payment · ${fmtINR(modal.payment.amount)}` : "Request more information"} onClose={() => setModal(null)}>
          <p className="text-sm text-muted-foreground">
            {modal.mode === "reject"
              ? `Reject ${modal.payment.customer_name}'s payment submission? The customer will be notified and can resubmit.`
              : `Ask ${modal.payment.customer_name} for additional payment details.`}
          </p>
          <textarea data-testid="payment-note-input" value={note} onChange={(e) => setNote(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm min-h-24" placeholder="Note for the customer (optional but recommended)" />
          <div className="flex gap-3 justify-end">
            <Button variant="outline" data-testid="payment-note-cancel" onClick={() => setModal(null)} className="rounded-full font-bold">Cancel</Button>
            <Button data-testid="payment-note-confirm" disabled={busy} onClick={submitNote}
              className={`rounded-full font-bold ${modal.mode === "reject" ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}`}>
              {busy ? "Working..." : "Confirm"}
            </Button>
          </div>
        </Modal>
      )}

      {modal?.mode === "payout" && (
        <Modal testid="payout-modal" title={`Record payout · ${modal.partner.partner_name}`} onClose={() => setModal(null)}>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="overline-label">Amount (₹) — pending {fmtINR(modal.partner.pending)}</Label>
              <Input data-testid="payout-amount" type="number" min="1" max={modal.partner.pending} value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} className="h-12 rounded-md bg-background" />
            </div>
            <div className="space-y-1.5">
              <Label className="overline-label">Method</Label>
              <Input data-testid="payout-method" value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })} className="h-12 rounded-md bg-background" placeholder="UPI / Bank Transfer" />
            </div>
            <div className="space-y-1.5">
              <Label className="overline-label">Reference / UTR</Label>
              <Input data-testid="payout-reference" value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} className="h-12 rounded-md bg-background" />
            </div>
            <div className="space-y-1.5">
              <Label className="overline-label">Notes</Label>
              <Input data-testid="payout-notes" value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} className="h-12 rounded-md bg-background" />
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="outline" data-testid="payout-cancel" onClick={() => setModal(null)} className="rounded-full font-bold">Cancel</Button>
            <Button data-testid="payout-confirm" disabled={busy || !payForm.amount} onClick={recordPayout} className="rounded-full font-bold">{busy ? "Saving..." : "Record Payout"}</Button>
          </div>
        </Modal>
      )}
    </div>
  );
}
